import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
// Fare DEĞİL işaretçi olayları — `TradeChart` ile aynı gerekçe: dokunmatikte
// `mousemove` yalnız dokunuş bittikten sonra taklit ediliyor, parmak
// sürüklenirken hiç gelmiyor. Bu grafik mobil İşlem ekranına da girdi
// (2026-08-03), fare olaylarıyla orada ölü bir yüzey olurdu.
import type { PointerEvent } from 'react'
import { price as fmtPrice, qty } from '../format'
import type { Depth, Level } from '../types'

/**
 * Derinlik grafiği — kümülatif emir hacminin fiyata göre dağılımı.
 *
 * Y ekseni "bu fiyata kadar süpürürsem toplam ne kadar alırım/satarım" değeridir;
 * yani `Emir Tablosu` sekmesindeki `K.Miktar` sütununun grafiği. Alış eğrisi en
 * iyi alıştan aşağı doğru birikir (sola gidildikçe yükselir), satış eğrisi en iyi
 * satıştan yukarı (sağa gidildikçe yükselir). Aradaki boşluk makastır.
 *
 * Renkler uygulamanın geri kalanıyla aynı: alış yeşil, satış kırmızı, PTF
 * kehribar kesikli çizgi (eşleşme grafiğindeki PTF çizgisiyle aynı gramer —
 * yeşil kullanılsaydı "alış" rengiyle çakışırdı).
 *
 * Sayısal karşılığı `Emir Tablosu` sekmesinde durur: bu grafiğin tablo ikizi
 * orasıdır, tooltip hiçbir veriyi tek başına tutmaz.
 */

interface Props {
  depth: Depth | null
  mcp?: number | null
}

const PAD = { top: 10, right: 12, bottom: 18, left: 44 }

interface Point {
  price: number
  cum: number
  x: number
  y: number
}

interface Side {
  points: Point[]
  area: string
  line: string
}

interface Layout {
  bid: Side | null
  ask: Side | null
  all: Point[]
  plotWidth: number
  plotHeight: number
  floor: number
  xTicks: number[]
  yTicks: number[]
  x: (p: number) => number
  y: (v: number) => number
}

/** Her seviyeye kadar biriken toplam miktar (tablodaki K.Miktar ile aynı). */
function cumulative(levels: Level[]): number[] {
  let running = 0
  return levels.map((level) => (running += level.quantity))
}

export function DepthChart({ depth, mcp = null }: Props) {
  const boxRef = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ width: 0, height: 0 })
  const [hover, setHover] = useState<Point | null>(null)

  // Ölçü her render'da yenileniyor — tek seferlik mount ölçüsü, kutu geç
  // boyutlandığında sıfırda kalıp grafiği kalıcı olarak boş bırakıyor.
  const measure = () => {
    const el = boxRef.current
    if (!el) return
    const { width, height } = el.getBoundingClientRect()
    setSize((prev) => (prev.width === width && prev.height === height ? prev : { width, height }))
  }
  useLayoutEffect(measure)

  useEffect(() => {
    const el = boxRef.current
    if (!el) return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const layout = useMemo<Layout | null>(() => {
    const { width, height } = size
    const bids = depth?.bids ?? []
    const asks = depth?.asks ?? []
    if (width < 140 || height < 100 || (bids.length === 0 && asks.length === 0)) return null

    const plotWidth = width - PAD.left - PAD.right
    const plotHeight = height - PAD.top - PAD.bottom
    const floor = PAD.top + plotHeight

    const bidCum = cumulative(bids)
    const askCum = cumulative(asks)

    const fiyatlar = [...bids.map((l) => l.price), ...asks.map((l) => l.price)]
    if (mcp != null) fiyatlar.push(mcp) // PTF eksen dışında kalıp görünmez olmasın
    const pMin = Math.min(...fiyatlar)
    const pMax = Math.max(...fiyatlar)
    const span = pMax - pMin || 1

    const vMax = Math.max(bidCum[bidCum.length - 1] ?? 0, askCum[askCum.length - 1] ?? 0, 1)

    const x = (p: number) => PAD.left + ((p - pMin) / span) * plotWidth
    const y = (v: number) => floor - (v / vMax) * plotHeight

    /** En iyi fiyattan dışa doğru merdiven; alan tabana kapatılır. */
    const build = (levels: Level[], cum: number[]): Side | null => {
      if (levels.length === 0) return null
      const points: Point[] = levels.map((level, i) => ({
        price: level.price,
        cum: cum[i]!,
        x: x(level.price),
        y: y(cum[i]!),
      }))

      // Basamak: değer bir sonraki seviyeye kadar sabit kalır.
      const steps: string[] = [`M${points[0]!.x} ${floor}`, `L${points[0]!.x} ${points[0]!.y}`]
      for (let i = 1; i < points.length; i++) {
        steps.push(`L${points[i]!.x} ${points[i - 1]!.y}`)
        steps.push(`L${points[i]!.x} ${points[i]!.y}`)
      }
      const line = steps.join(' ')
      const son = points[points.length - 1]!
      return { points, line, area: `${line} L${son.x} ${floor} Z` }
    }

    // Bir kez kurulup paylaşılıyor: `all` içindeki noktalar taraf dizileriyle
    // AYNI nesneler olmalı, hover'ın hangi tarafta olduğu kimlikle bulunuyor.
    const bid = build(bids, bidCum)
    const ask = build(asks, askCum)

    return {
      bid,
      ask,
      all: [...(bid?.points ?? []), ...(ask?.points ?? [])],
      plotWidth,
      plotHeight,
      floor,
      // `niceTicks` adımı yukarı yuvarladığı için istenenden az değer döner;
      // dar panelde bile 3-4 çizgi kalsın diye biraz fazla isteniyor.
      xTicks: niceTicks(pMin, pMax, 4),
      yTicks: niceTicks(0, vMax, 5).filter((v) => v > 0),
      x,
      y,
    }
  }, [size, depth, mcp])

  const handleMove = useCallback(
    (event: PointerEvent<SVGSVGElement>) => {
      if (!layout) return
      const rect = event.currentTarget.getBoundingClientRect()
      const mouseX = event.clientX - rect.left
      let nearest = layout.all[0]
      for (const point of layout.all) {
        if (!nearest || Math.abs(point.x - mouseX) < Math.abs(nearest.x - mouseX)) nearest = point
      }
      setHover(nearest ?? null)
    },
    [layout],
  )

  if (!depth) return <div className="tab-empty">Derinlik bekleniyor…</div>

  const hoverSide =
    hover && layout?.bid?.points.includes(hover) ? 'bid' : hover ? 'ask' : null

  return (
    <>
      <div className="depth-legend">
        <span className="legend-key bid">
          <i /> Alış
        </span>
        <span className="legend-key ask">
          <i /> Satış
        </span>
        {mcp != null && (
          <span className="legend-key ptf">
            <i /> PTF
          </span>
        )}
      </div>

      <div className="chart-box" ref={boxRef}>
        {!layout ? (
          <div className="chart-empty">Çiziliyor…</div>
        ) : (
          <svg
            className="chart-svg"
            viewBox={`0 0 ${size.width} ${size.height}`}
            preserveAspectRatio="none"
            onPointerMove={handleMove}
            // Parmak basar basmaz okuma çıksın — ayrıca bir "hareket" beklemeye
            // gerek yok.
            onPointerDown={handleMove}
            onPointerLeave={(event) => {
              // Parmak kalkınca `pointerleave` de tetikleniyor; dokunmatikte
              // balonu orada kapatmak okumaya fırsat bırakmazdı.
              if (event.pointerType !== 'mouse') return
              setHover(null)
            }}
            role="img"
            aria-label="Kümülatif emir defteri derinliği"
          >
            {layout.yTicks.map((tick) => (
              <g key={`y${tick}`}>
                <line
                  className="chart-grid"
                  x1={PAD.left}
                  x2={PAD.left + layout.plotWidth}
                  y1={layout.y(tick)}
                  y2={layout.y(tick)}
                />
                <text className="chart-tick" x={PAD.left - 6} y={layout.y(tick) + 3}>
                  {qty(tick)}
                </text>
              </g>
            ))}

            {layout.bid && (
              <>
                <path className="depth-area bid" d={layout.bid.area} />
                <path className="depth-line bid" d={layout.bid.line} />
              </>
            )}
            {layout.ask && (
              <>
                <path className="depth-area ask" d={layout.ask.area} />
                <path className="depth-line ask" d={layout.ask.line} />
              </>
            )}

            {/* PTF — eşleşme grafiğindeki ile aynı kesikli kehribar çizgi */}
            {mcp != null && (
              <line
                className="chart-ptf"
                x1={layout.x(mcp)}
                x2={layout.x(mcp)}
                y1={PAD.top}
                y2={layout.floor}
              />
            )}

            {hover && (
              <g>
                <line
                  className="chart-crosshair"
                  x1={hover.x}
                  x2={hover.x}
                  y1={PAD.top}
                  y2={layout.floor}
                />
                <circle className={`depth-dot ${hoverSide}`} cx={hover.x} cy={hover.y} r={3.5} />
              </g>
            )}

            {layout.xTicks.map((tick, i) => (
              <text
                key={`x${tick}`}
                className="chart-tick"
                x={layout.x(tick)}
                y={size.height - 5}
                textAnchor={i === 0 ? 'start' : i === layout.xTicks.length - 1 ? 'end' : 'middle'}
              >
                {/* Eksende kuruş gürültü; tam değer tooltip'te. */}
                {qty(tick)}
              </text>
            ))}
          </svg>
        )}

        {hover && layout && (
          <div
            className="chart-tooltip"
            style={{
              left: Math.min(Math.max(hover.x + 10, 6), Math.max(size.width - 128, 6)),
              top: Math.max(hover.y - 44, 6),
            }}
          >
            <div className="tooltip-time">{hoverSide === 'bid' ? 'Alış' : 'Satış'}</div>
            <div className="tooltip-row">
              <span>Fiyat</span>
              <b>{fmtPrice(hover.price)}</b>
            </div>
            <div className="tooltip-row">
              <span>K.Miktar</span>
              <b>{qty(hover.cum)}</b>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

/** Yuvarlak sayılara oturan eksen değerleri (1 / 2 / 2.5 / 5 katları). */
function niceTicks(min: number, max: number, count: number): number[] {
  const raw = (max - min) / count
  if (!Number.isFinite(raw) || raw <= 0) return []
  const magnitude = 10 ** Math.floor(Math.log10(raw))
  const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((s) => s >= raw) ?? magnitude * 10
  const ticks: number[] = []
  for (let v = Math.ceil(min / step) * step; v <= max; v += step) ticks.push(v)
  return ticks
}
